<template>
  <div class="financingOffers">
    <div class="d-flex align-items-center mb-3">
      <label :for="totalDownPaymentId" class="mb-0 mr-2">
        <strong>Total Down Payment</strong>
      </label>
      <TextField
        :id="totalDownPaymentId"
        v-model="totalDownPaymentCents"
        data-cy="totalDownPayment"
        currency
        clearable
        style="width: 120px"
        class="mb-0 payment-amount-field"
        no-label
        :disabled="noLoansAvailableAtZeroDown"
        :error-message="firstErrorMessage(v$.totalDownPaymentCents)"
      />
      <div v-if="selectedLoanOffer" class="loanTotals ml-auto">
        <div class="d-flex align-items-center ml-auto">
          <strong class="mr-2">Total Finance Amount: </strong>
          <div data-cy="totalFinanceAmount">
            {{ dollars(selectedLoanOffer.loan_amount_cents) }}
          </div>
        </div>
        <div>
          <div
            v-if="selectedLoanOffer.promotional_payment_summary"
            class="promoOffer d-flex align-items-center mb-2"
          >
            <strong class="mr-2">Promo: </strong>
            <div data-cy="promoDetails">
              {{ selectedLoanOffer.promotional_payment_summary }}
            </div>
          </div>
          <div class="d-flex align-items-center">
            <strong class="mr-2">Regular: </strong>
            <div data-cy="regularDetails">
              {{ selectedLoanOffer.payment_summary }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <Card v-if="loadingPrograms" headless>
      <div class="d-flex align-items-center">
        <Spinner2
          class="ml-auto mr-auto"
          :size="40"
          color="primary"
          message="Loading Available Programs..."
        />
      </div>
    </Card>
    <div v-else>
      <div v-if="!vendorLoanOffers.length">
        No loan programs available for the total amount specified.
      </div>
      <Card
        v-for="vendorLoanOffer in vendorLoanOffers"
        :key="vendorLoanOffer.lender"
        headless
        no-shadow
      >
        <FinancingOptions
          class="vendorFinanceOptions mb-2"
          :lender="vendorLoanOffer.lender"
          :loan-offers="vendorLoanOffer.loan_offers"
          data-cy="vendorLoanOption"
        />
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: "FinancingOffers"
};
</script>

<script lang="ts" setup>
import { computed } from "vue";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Card from "@/components/Card.vue";
import Spinner2 from "@/components/Spinner2.vue";
import TextField from "@/components/TextField.vue";
import useVuelidate from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import FinancingOptions from "./FinancingOptions.vue";
import { useFinancingAvailable } from "./useFinancingAvailable";

const {
  quote,
  vendorLoanOffers,
  loadingPrograms,
  totalDownPaymentCents,
  selectedLoanOffer
} = useFinancingAvailable();

const totalDownPaymentId = `totalDownPayment_${new Date().getTime()}`;

const v$ = useVuelidate(
  {
    totalDownPaymentCents: {
      $autoDirty: true,
      minValue: helpers.withMessage("Invalid amount", (val: number) => {
        return val >= 0;
      }),
      maxValue: helpers.withMessage("Invalid amount", (val: number) => {
        return val < quote.value.financials.total_cents;
      })
    }
  },
  { totalDownPaymentCents }
);

const noLoansAvailableAtZeroDown = computed(() => {
  return (
    totalDownPaymentCents.value === 0 && vendorLoanOffers.value?.length === 0
  );
});
</script>

<style lang="scss" scoped>
.financingOffers {
  .vendorFinanceOptions :deep(.radio-group) {
    max-height: 310px;
    overflow-y: auto;
  }
  .loanTotals {
    display: flex;
    align-items: center;
    > * {
      &:not(:last-child) {
        &:after {
          content: " ";
          background: #c9cdd0;
          width: 1px;
          height: 50px;
          margin-left: 30px;
          margin-right: 30px;
        }
      }
    }
  }
  @media (max-width: 1200px) {
    .loanTotals {
      margin-left: 30px;
      display: block;
      white-space: nowrap;
      border-left: 1px solid #c9cdd0;
      padding-left: 24px;
      padding-top: 3px;
      padding-bottom: 3px;
      .promoOffer {
        margin-bottom: 0;
      }
      > * {
        margin-bottom: 10px;
        &:last-child {
          margin-bottom: 0;
        }
        &:not(:last-child) {
          &:after {
            display: none;
            margin-left: 0;
            margin-right: 0;
          }
        }
      }
    }
  }
}
</style>
